import { Box3, Group, Vector3 } from "three";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { ModelAsset } from "../models/model-assets";

export type RollerLiftTransferOptions = {
  height?: number;
  length?: number;
  position?: { x?: number; z?: number };
  rotationY?: number;
  width?: number;
};

type AlignedPart = {
  content: Group;
  object: Group;
  size: Vector3;
};

const defaultOptions = {
  height: 0.44,
  length: 1.701,
  rotationY: 0,
  width: 1.3314
};
const rollerRotationY = Math.PI / 2;

export class RollerLiftTransfer extends Group {
  private readonly length: number;
  private readonly structure = new Group();
  private readonly width: number;

  constructor({
    height = defaultOptions.height,
    length = defaultOptions.length,
    position = {},
    rotationY = defaultOptions.rotationY,
    width = defaultOptions.width
  }: RollerLiftTransferOptions = {}) {
    super();
    this.length = validatePositiveNumber(length, "length");
    this.width = validatePositiveNumber(width, "width");
    if (!Number.isFinite(rotationY)) {
      throw new RangeError("rotationY must be finite");
    }

    this.name = "roller-lift-transfer";
    this.structure.name = "roller-lift-transfer-structure";
    this.position.set(position.x ?? 0, validateFiniteNumber(height, "height"), position.z ?? 0);
    this.rotation.y = rotationY;
    this.add(this.structure);
  }

  static async create(
    options: RollerLiftTransferOptions = {},
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const transfer = new RollerLiftTransfer(options);
    await transfer.loadStructure(loader);
    return transfer;
  }

  getHeight() {
    return this.position.y;
  }

  setHeight(height: number) {
    this.position.y = validateFiniteNumber(height, "height");
  }

  private async loadStructure(loader: ModelAssetLoader) {
    const [backCrossbar, frontCrossbar, rollerTemplate] = await Promise.all([
      loadAlignedPart(
        loader,
        ModelAsset.RollerLiftTransferCrossbar,
        "roller-lift-transfer-crossbar-back"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.RollerLiftTransferCrossbar,
        "roller-lift-transfer-crossbar-front"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.RollerLiftTransferRoller,
        "roller-lift-transfer-roller-template"
      )
    ]);

    const halfCrossbarSpanZ = Math.max(0, this.width / 2 - backCrossbar.size.x / 2);
    const crossbarScaleZ = this.length / backCrossbar.size.z;
    [backCrossbar, frontCrossbar].forEach((crossbar, index) => {
      const isBack = index === 0;
      crossbar.object.position.set(0, 0, isBack ? -halfCrossbarSpanZ : halfCrossbarSpanZ);
      crossbar.content.rotation.y = isBack ? -Math.PI / 2 : Math.PI / 2;
      crossbar.content.scale.z = crossbarScaleZ;
    });

    const rollerDiameter = rollerTemplate.size.z;
    const rollerTargetLength = Math.max(0, this.width - backCrossbar.size.x * 2);
    const rollerScaleX = rollerTargetLength / rollerTemplate.size.x;
    const rollerStartX = -this.length / 2 + rollerDiameter / 2;
    const rollerEndX = this.length / 2 - rollerDiameter / 2;
    const rollerPositions = createCenteredPositions(rollerStartX, rollerEndX, rollerDiameter * 2.5);
    const rollers = await Promise.all(
      rollerPositions.map(async (x, index) => {
        const roller =
          index === 0
            ? rollerTemplate
            : await loadAlignedPart(
                loader,
                ModelAsset.RollerLiftTransferRoller,
                `roller-lift-transfer-roller-${index}`
              );
        roller.object.name = `roller-lift-transfer-roller-${index}`;
        roller.object.position.set(x, 0, 0);
        roller.content.rotation.y = rollerRotationY;
        roller.content.scale.x = rollerScaleX;
        return roller.object;
      })
    );

    this.structure.add(backCrossbar.object, frontCrossbar.object, ...rollers);
  }
}

function createCenteredPositions(start: number, end: number, pitch: number) {
  if (end <= start || pitch <= 0) {
    return [(start + end) / 2];
  }
  const count = Math.floor((end - start) / pitch) + 1;
  if (count <= 1) {
    return [(start + end) / 2];
  }
  const firstPosition = (start + end - pitch * (count - 1)) / 2;
  return Array.from({ length: count }, (_, index) => firstPosition + pitch * index);
}

async function loadAlignedPart(
  loader: ModelAssetLoader,
  asset: ModelAsset,
  name: string
): Promise<AlignedPart> {
  const model = await loader.load(asset);
  model.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(model);
  const center = bounds.getCenter(new Vector3());
  const size = bounds.getSize(new Vector3());
  const object = new Group();
  const content = new Group();

  model.position.set(
    model.position.x - center.x,
    model.position.y - center.y,
    model.position.z - center.z
  );
  object.name = name;
  content.add(model);
  object.add(content);
  return { content, object, size };
}

function validatePositiveNumber(value: number, label: string) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${label} must be a positive finite number`);
  }
  return value;
}

function validateFiniteNumber(value: number, label: string) {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${label} must be finite`);
  }
  return value;
}
